"use client";

import type { ColumnDef } from "@tanstack/react-table";

import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  PURCHASE_REQUEST_STATUS_COLORS,
  purchaseRequestStatusLabel,
} from "@/constants/purchase-request";
import { DataTableColumnHeader } from "@/lib/data-table-column-header";
import type { PurchaseRequest, UserLite } from "@/types";
import { formatDateAR } from "@/utils/date";
import { truncateMiddle } from "@/utils/text";

function formatAssignedUser(user?: UserLite | null) {
  if (!user) return "Sin asignar";
  const fullName = user.full_name?.trim();
  if (fullName) {
    return fullName;
  }
  return user.email;
}

function renderWithTooltip(label: string, maxLength = 22) {
  if (!label) return "-";
  if (label.length <= maxLength) {
    return label;
  }

  const truncated = truncateMiddle(label, maxLength);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-help">{truncated}</span>
        </TooltipTrigger>
        <TooltipContent>
          <p className="max-w-xs">{label}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export const columns: ColumnDef<PurchaseRequest>[] = [
  {
    accessorKey: "id",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="ID" />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "request_number",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Número de Solicitud" />
    ),
  },
  {
    accessorKey: "sector.name",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Sector" />
    ),
    cell: ({ row }) => {
      const sector = row.original.sector;
      const sectorName = sector?.name;
      
      if (!sectorName) {
        return <div>-</div>;
      }

      const truncatedName = truncateMiddle(sectorName, 20);
      const shouldShowTooltip = sectorName.length > 20;

      if (shouldShowTooltip) {
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="cursor-help">{truncatedName}</div>
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs">{sectorName}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      }

      return <div>{sectorName}</div>;
    },
  },
  {
    accessorKey: "requester",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Comprador" />
    ),
    enableSorting: false,
    cell: ({ row }) => {
      const requesterValue = row.getValue("requester");
      const requester = typeof requesterValue === 'string' ? requesterValue.trim() : '';
      const buyer = row.original.assignments?.buyer;
      const buyerLabel = formatAssignedUser(buyer);
      const buyerContent = renderWithTooltip(buyerLabel);
      const requesterContent = renderWithTooltip(requester ?? "-");

      return (
        <div className="flex flex-col gap-1">
          <div className="font-medium text-foreground">{buyerContent}</div>
          <div className="text-xs text-muted-foreground">
            Solicitante: {requesterContent}
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "total_items",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Items" />
    ),
    cell: ({ row }) => {
      const total = row.getValue("total_items");
      return <div className="text-center">{Number(total ?? 0)}</div>;
    },
  },
  {
    accessorKey: "priority",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Prioridad" />
    ),
    cell: ({ row }) => {
      const priority = row.getValue("priority");
      return priority ? (
        <Badge variant="destructive">Alta</Badge>
      ) : (
        <Badge variant="secondary">Normal</Badge>
      );
    },
  },
  {
    accessorKey: "status",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Estado" />
    ),
    cell: ({ row }) => {
      const status = row.getValue("status");
      const color =
        PURCHASE_REQUEST_STATUS_COLORS[
          status as keyof typeof PURCHASE_REQUEST_STATUS_COLORS
        ] ?? "";
      return (
        <Badge className={color}>{purchaseRequestStatusLabel(status as string)}</Badge>
      );
    },
  },
  {
    accessorKey: "total_quotes",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Total Cotizaciones" />
    ),
    cell: ({ row }) => {
      const total = row.getValue("total_quotes");
      return <div className="text-center">{Number(total ?? 0)}</div>;
    },
  },
  {
    accessorKey: "created_at",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Fecha de creación" />
    ),
    cell: ({ row }) => {
      const date = row.getValue("created_at");
      return <div>{formatDateAR(date as string | number | Date)}</div>;
    },
  },
];